import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { BrainCircuit, Sparkles, ArrowRight } from 'lucide-react';

const ABC_STYLE = {
  A: 'bg-red-100 text-red-700',
  B: 'bg-amber-100 text-amber-700',
  C: 'bg-slate-100 text-slate-600',
};
const CONF_STYLE = {
  alta: 'bg-green-100 text-green-700',
  'média': 'bg-amber-100 text-amber-700',
  baixa: 'bg-red-100 text-red-700',
};
const MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const PARAM_LABELS = {
  reorder_point: 'Ponto de reencomenda (ROP)',
  safety_stock: 'Safety stock',
  optimal_stock: 'Stock ótimo',
  moq: 'MOQ',
};

const Stat = ({ label, value, suffix }) => (
  <div className="bg-slate-50 rounded p-2">
    <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="font-semibold text-slate-800 text-sm">
      {value ?? '—'}{value !== null && value !== undefined && suffix ? ` ${suffix}` : ''}
    </p>
  </div>
);

const eur = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}€`);

export const PriceStrip = ({ prices }) => {
  if (!prices) return null;
  const marginPositive = (prices.margin_pct ?? 0) >= 0;
  const hasCost = prices.avg_cost_is_real && prices.avg_cost != null;
  const hasSupplierPrice = prices.supplier_pricing?.net_price != null;
  const hasEstimatedMargin = prices.estimated_margin_pct != null;
  
  // Determine which margin to show and its source
  const showRealMargin = prices.margin_pct != null;
  const marginValue = showRealMargin ? prices.margin_pct : prices.estimated_margin_pct;
  const marginEur = showRealMargin ? prices.margin_eur : prices.estimated_margin_eur;
  const marginIsEstimate = !showRealMargin && hasEstimatedMargin;
  
  return (
    <div className="space-y-2" data-testid="price-strip">
      {/* Main price row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className={`rounded-md p-2.5 border ${hasCost ? 'bg-slate-50 border-slate-100' : 'bg-amber-50 border-amber-100'}`}>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">
            Custo Médio {hasCost ? '(PCMP)' : ''}
          </p>
          <p className={`font-mono font-semibold ${hasCost ? 'text-slate-800' : 'text-amber-600'}`} data-testid="price-avg-cost">
            {hasCost ? eur(prices.avg_cost) : 'N/D'}
          </p>
        </div>
        <div className="bg-slate-50 rounded-md p-2.5 border border-slate-100">
          <p className="text-[10px] uppercase tracking-wide text-slate-400">
            Últ. Preço Compra
            {prices.last_purchase_doc_type && <span className="text-[8px] ml-1">({prices.last_purchase_doc_type})</span>}
          </p>
          <p className="font-mono font-semibold text-slate-800" data-testid="price-last-buy">
            {prices.last_purchase_price ? eur(prices.last_purchase_price) : '—'}
          </p>
          {prices.last_purchase_discount_pct > 0 && (
            <p className="text-[9px] text-slate-400">
              {eur(prices.last_purchase_price_gross)} -{prices.last_purchase_discount_pct}%
            </p>
          )}
        </div>
        <div className="bg-slate-50 rounded-md p-2.5 border border-slate-100">
          <p className="text-[10px] uppercase tracking-wide text-slate-400">PVP1 c/IVA</p>
          <p className="font-mono font-semibold text-slate-800" data-testid="price-pvp">{eur(prices.pvp_with_vat)}</p>
        </div>
        <div className={`rounded-md p-2.5 border ${
          marginValue === null || marginValue === undefined
            ? 'bg-amber-50 border-amber-100'
            : marginPositive ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'
        }`}>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">
            Margem (s/IVA) {marginIsEstimate && <span className="text-amber-500">*</span>}
          </p>
          <p className={`font-mono font-semibold ${
            marginValue === null || marginValue === undefined
              ? 'text-amber-600' 
              : marginPositive ? 'text-green-700' : 'text-red-700'
          }`} data-testid="price-margin">
            {marginValue === null || marginValue === undefined
              ? 'Custo indisponível' 
              : `${marginValue}% · ${eur(marginEur)}`}
          </p>
          {marginIsEstimate && (
            <p className="text-[9px] text-amber-500">
              *Estimada ({prices.estimated_margin_source === 'supplier_enf' ? 'preço fornecedor' : 'últ. compra'})
            </p>
          )}
        </div>
      </div>
      
      {/* Supplier pricing row (from ENF) */}
      {hasSupplierPrice && (
        <div className="bg-blue-50 rounded-md p-2.5 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-blue-500">
                Preço Líquido Fornecedor (Encomenda)
              </p>
              <p className="font-mono font-semibold text-blue-700" data-testid="price-supplier-net">
                {eur(prices.supplier_pricing.net_price)}
                {prices.supplier_pricing.discount_pct > 0 && (
                  <span className="text-[11px] font-normal text-blue-500 ml-2">
                    ({eur(prices.supplier_pricing.gross_price)} -{prices.supplier_pricing.discount_pct}%)
                  </span>
                )}
              </p>
            </div>
            {prices.supplier_pricing.doc_ref && (
              <p className="text-[9px] text-blue-400">
                Fonte: {prices.supplier_pricing.doc_ref}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SourceBadge = ({ source }) => {
  if (source === 'manual')
    return <Badge className="bg-blue-100 text-blue-700 text-[10px]">Manual (vence)</Badge>;
  if (source === 'ai')
    return <Badge className="bg-violet-100 text-violet-700 text-[10px]">AI</Badge>;
  return <Badge variant="outline" className="text-slate-400 text-[10px]">Não definido</Badge>;
};

export const FichaProcurement = ({ sku, onPrices }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/procurement/ficha/${sku}`)
      .then((res) => { setData(res.data); onPrices?.(res.data.prices || null); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [sku]);

  if (loading) return null;
  if (!data) return null;

  const { product: p, resolved, params, pending_suggestion: sug, coverage_days, prices } = data;
  const conf = params?.confidence;
  const explain = params?.explain;
  const monthlyIdx = params?.monthly_index;
  const maxIdx = monthlyIdx ? Math.max(...Object.values(monthlyIdx), 1) : 1;

  return (
    <Card className="lg:col-span-3 border border-slate-200 rounded-sm shadow-none" data-testid="ficha-procurement">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BrainCircuit className="h-5 w-5 text-violet-600" />
          Ficha de Procurement
        </CardTitle>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {p.abc_class && (
            <Badge className={`${ABC_STYLE[p.abc_class] || ABC_STYLE.C} text-[10px]`} data-testid="ficha-abc">
              Classe {p.abc_class}
            </Badge>
          )}
          {conf?.label && (
            <Badge className={`${CONF_STYLE[conf.label] || CONF_STYLE.baixa} text-[10px]`} data-testid="ficha-confidence">
              Confiança {conf.label} ({conf.score})
            </Badge>
          )}
          {params && (
            <Badge variant="outline" className="text-[10px]" data-testid="ficha-eligible">
              {params.eligible ? '✓ Elegível para gestão ativa' : 'Não elegível'}
            </Badge>
          )}
          {params?.seasonal && <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">Sazonal</Badge>}
          {p.ai_tracking_enabled
            ? <Badge className="bg-green-100 text-green-700 text-[10px]">AI tracking ativo</Badge>
            : <Badge variant="outline" className="text-slate-500 text-[10px]">Sem AI tracking</Badge>}
          {p.excluded_from_procurement && (
            <Badge className="bg-red-100 text-red-700 text-[10px]">Excluído de procurement</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <PriceStrip prices={prices} />

        {sug && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-violet-200 bg-violet-50/60 px-3 py-2"
               data-testid="ficha-suggestion-callout">
            <p className="text-sm text-violet-800">
              <Sparkles className="h-4 w-4 inline mr-1" />
              Sugestão AI pendente para este artigo: ROP {sug.proposed?.reorder_point} · SS {sug.proposed?.safety_stock}
              {sug.delta_pct !== null && sug.delta_pct !== undefined ? ` · Δ ${sug.delta_pct}%` : ''}
            </p>
            <Link to="/cockpit">
              <Button size="sm" variant="outline" className="border-violet-300 text-violet-700" data-testid="ficha-suggestion-cta">
                Rever no Cockpit <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </Link>
          </div>
        )}

        {!params ? (
          <p className="text-sm text-slate-500" data-testid="ficha-no-analysis">
            Sem análise de procura — este artigo não teve vendas reais nos últimos 24 meses
            (ou a análise ainda não correu).
          </p>
        ) : (
          <>
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                Procura real ({params.months_active} meses ativos)
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                <Stat label="Consumo médio / dia" value={params.avg_daily_qty?.toFixed(2)} />
                <Stat label="Consumo / semana" value={params.avg_weekly_qty?.toFixed(1)} />
                <Stat label="Consumo / mês" value={Math.round(params.avg_monthly_qty || 0)} />
                <Stat label="Variabilidade (CV)" value={params.variability_cv} />
                <Stat label="Cobertura atual" value={coverage_days} suffix="dias" />
                <Stat label="Dias com venda (12m)" value={params.sale_days_12m} />
                <Stat label="Qtd vendida (12m)" value={Math.round(params.qty_12m || 0)} suffix="un." />
                <Stat label="Intervalo mediano" value={params.median_interval_days} suffix="dias" />
                <Stat label="Primeira venda" value={params.first_sale} />
                <Stat label="Última venda" value={params.last_sale} />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                Parâmetros em vigor <span className="normal-case font-normal text-slate-400">(manual vence sempre o AI)</span>
              </p>
              <div className="border rounded-md divide-y">
                {Object.entries(PARAM_LABELS).map(([k, label]) => {
                  const r = resolved[k] || {};
                  return (
                    <div key={k} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`ficha-param-${k}`}>
                      <span className="text-slate-600">{label}</span>
                      <span className="flex items-center gap-2">
                        {r.source === 'manual' && r.ai_value !== null && r.ai_value !== undefined && (
                          <span className="text-xs text-slate-400">AI: {r.ai_value}</span>
                        )}
                        <strong className="font-mono">{r.value ?? '—'}</strong>
                        <SourceBadge source={r.source} />
                      </span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-3 py-2 text-sm" data-testid="ficha-param-lead_time">
                  <span className="text-slate-600">Lead time</span>
                  <span className="flex items-center gap-2">
                    <strong className="font-mono">{p.lead_time_days ?? '—'}{p.lead_time_days ? 'd' : ''}</strong>
                    {explain?.defaults_used?.lead_time_days
                      ? <Badge variant="outline" className="text-amber-600 border-amber-200 text-[10px]">Default (15d) — configurar fornecedor</Badge>
                      : <Badge variant="outline" className="text-slate-500 text-[10px]">Fornecedor</Badge>}
                  </span>
                </div>
              </div>
            </div>

            {monthlyIdx && (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                  Índice de sazonalidade <span className="normal-case font-normal text-slate-400">(1.0 = mês médio)</span>
                </p>
                <div className="flex items-end gap-1 h-20" data-testid="ficha-seasonality">
                  {MONTH_LABELS.map((ml, i) => {
                    const v = monthlyIdx[String(i + 1)];
                    const h = v ? Math.round((v / maxIdx) * 52) + 4 : 0;
                    return (
                      <div key={`month-${i + 1}`} className="flex-1 flex flex-col items-center justify-end gap-0.5 h-full" title={v ? `${ml}: ${v}` : ml}>
                        <span className="text-[9px] text-slate-500">{v ?? ''}</span>
                        <div className={`w-full rounded-sm ${v >= 1.5 ? 'bg-cyan-500' : 'bg-slate-300'}`}
                             style={{ height: `${h}px` }} />
                        <span className="text-[9px] text-slate-400">{ml}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="text-xs text-slate-500 space-y-1 border-t pt-3" data-testid="ficha-explain">
              <p><strong>Porquê estes valores?</strong> ROP = consumo diário × lead time + safety stock (z·σ·√LT).
                Classe {params.abc_class} usa nível de serviço z={params.service_z ?? '—'}.</p>
              <p><strong>Base:</strong> {explain?.docs_analyzed} documentos de venda reais ({explain?.lines_analyzed} linhas)
                entre {explain?.period?.start} e {explain?.period?.end}. Movimentos internos (TAR/GR/ajustes) ignorados.</p>
              <p><strong>Algoritmo:</strong> {explain?.algorithm} · <strong>Calculado em:</strong>{' '}
                {explain?.computed_at?.slice(0, 16).replace('T', ' ')}</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
